<template>
  <!-- Table Container Card -->
  <b-card no-body>

    <div class="m-2">

      <!-- Table Top -->
      <b-row>
        <!-- Per Page -->
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <TableEntries
            v-model="perPage"
            :options="perPageOptions"
          />
          <b-dropdown
            v-if="getUserPermission('EXPORT_ACCOUNT_HISTORY_REPORT')"
            id="dropdown-1"
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            :text="$t('Export')"
            variant="primary"
            :disabled="isDisabled"
          >
            <b-dropdown-item @click="csvExport">
              {{ $t('CSV') }}
            </b-dropdown-item>
            <b-dropdown-item @click="excelExport">
              {{ $t('Excel') }}
            </b-dropdown-item>
            <b-dropdown-item @click="printCustomerHistoryReport">
              {{ $t('PRINT') }}
            </b-dropdown-item>
          </b-dropdown>
        </b-col>
        <!-- Export -->
        <b-col
          cols="12"
          md="6"
        >
          <div class="d-flex align-items-center justify-content-lg-end">
            <Button
              :label="$t('Filter')"
              size="md"
              icon="FilterIcon"
              show-icon
              class="mr-1"
              @click="showModal('modal-filter')"
            />
            <Button
              :label="$t('Reset Filter')"
              size="md"
              icon="RotateCwIcon"
              show-icon
              @click="resetFilter"
            />
          </div>
        </b-col>
      </b-row>
    </div>

    <b-table
      ref="glTableRef"
      :items="reportData"
      :busy="isBusy"
      :per-page="pageSize"
      :current-page="currentPage"
      responsive
      :fields="translatedTableHeaders"
      primary-key="id"
      show-empty
      :empty-text="$t('No records available')"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      class="position-relative"
    >

      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            class="align-middle"
            variant="primary"
          />
        </div>
      </template>

      <template #cell(SN)="data">
        {{ getOverallIndex(data.index) }}
      </template>

      <template #cell(financialDate)="data">
        <p class="mb-0">
          {{ data.value ? formatCustomDate(data.value) : '' }}
        </p>
      </template>

      <template #cell(transactionDate)="data">
        <p class="mb-0">
          {{ data.value ? formatDateTime(data.value) : '' }}
        </p>
      </template>

      <!-- Column: account number -->
      <template #cell(accountNumber)="data">
        {{ data.value }}
      </template>

      <!-- Column: Account Name -->
      <template #cell(accountName)="data">
        {{ data.value }}
      </template>

      <template #cell(postedBy)="data">
        <span class="text-nowrap">
          {{ data.value }}
        </span>
      </template>

      <template #cell(postingReferenceNumber)="data">
        <span class="text-nowrap">
          {{ data.value }}
        </span>
      </template>

      <template #cell(debit)="data">
        <span class="text-nowrap">
          {{ data.value ? toBalance(data.value) : '' }}
        </span>
      </template>

      <template #cell(credit)="data">
        <span class="text-nowrap">
          {{ data.value ? toBalance(data.value) : '' }}
        </span>
      </template>

      <template #cell(balance)="data">
        <span class="text-nowrap">
          {{ data.value ? toBalance(data.value) : 0 }}
        </span>
      </template>

    </b-table>

    <TablePagination
      v-model="currentPage"
      :from="from"
      :to="to"
      :total-rows="totalEntries"
      :per-page="perPage"
    />

    <!-- modal -->
    <b-modal
      id="modal-filter"
      ref="my-modal"
      :ok-title="$t('Apply')"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      centered
      size="lg"
      no-close-on-backdrop
      :title="$t('Filters')"
      modal-footer
      @ok="handleSubmit"
      @cancel="resetFilters"
    >

      <validation-observer ref="accountBalanceHistoryRules">
        <b-form>
          <b-row class="match-height">
            <!-- Account Number -->
            <b-col md="6">
              <InputField
                id="account-number"
                v-model="filter.accountNumber"
                :label="$t('Account Number')"
                :placeholder="$t('Enter Account Number')"
                type="text"
              />
            </b-col>
            <!-- Account Number -->
            <!--  Reference Account Number -->
            <b-col md="6">
              <InputField
                id="reference-account-number"
                v-model="filter.referenceAccountNumber"
                :label="$t('Reference Account Number')"
                :placeholder="$t('Enter Reference Account Number')"
                type="text"
              />
            </b-col>
            <!--  Reference Account Number -->
          </b-row>

          <b-row class="match-height">
            <!-- Start Date -->
            <b-col md="6">
              <DatePickerField
                id="financial-date-from"
                v-model="filter.financialDateFrom"
                :min-date="new Date()"
                :label="$t('Financial Date (From)')"
                :config="datePickerConfig"
                :placeholder="$t('Date From')"
              />
            </b-col>
            <!-- Start Date -->
            <!-- End Date -->
            <b-col md="6">
              <DatePickerField
                id="financial-date-to"
                v-model="filter.financialDateTo"
                :min-date="new Date()"
                :label="$t('Financial Date (To)')"
                :config="datePickerConfig"
                :placeholder="$t('Date To')"
              />
            </b-col>
            <!-- End Date -->
          </b-row>
        </b-form>
      </validation-observer>

      <template
        v-if="isReadonly"
        #modal-footer="{ }"
      >
        <div />
      </template>
    </b-modal>
    <!-- modal -->
    <!-- Print Start -->
    <div
      ref="customerHistoryToPrint"
    >
      <p id="toPrinter">
        <b-row
          style="width: 100%; display: flex; margin-bottom: 30px;"
        >
          <b-col
            style="width: 70%; padding-left: 10px;"
          >
            <img
              style="width: 30% !important"
              :src="userDetails.institutionLogoURL ? userDetails.institutionLogoURL : reportInfo.orgLogo"
            >
            <p
              style="font-size: 16px; font-weight: 700"
              class="mt-1"
            >
              {{ corporateInformation.registeredName }}
            </p>
            <p
              style="font-size: 14px;"
              class="mt-0"
            >
              {{ corporateInformation.registeredAddress }}
            </p>
          </b-col>
          <b-col
            style="width: 30%"
          >
            <b-card-body>
              <b-card-text>
                <table
                  align="right"
                >
                  <tbody>

                    <p style="font-size: 16px; font-weight: 700;">
                      {{ $t('Account Balance History Report') }}
                    </p>
                    <tr>
                      <td>
                        <p
                          style="font-size: 14px;"
                          class="print-color"
                        >
                          {{ $t('Financial Date:') }}
                        </p>
                      </td>
                      <td v-if="filter.financialDateFrom && filter.financialDateTo">
                        <p style="font-size: 14px; font-weight: normal;">
                          {{ ` ${$t('From')}: ${filter.financialDateFrom} - ${$t('To')}: ${filter.financialDateTo} ` }}
                        </p>
                      </td>
                      <td v-else>
                        <p style="font-size: 15px; font-weight: normal;">
                          {{ $t('All') }}
                        </p>
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <p style="font-size: 14px; color: #000; margin-top: -10px;">
                          {{ $t('Account Name:') }}
                        </p></td>
                      <td><p style="font-size: 15px; font-weight: 600;">
                        {{ summary.accountName }}
                      </p></td>
                    </tr>
                    <tr>
                      <td>
                        <p style="font-size: 14px; color: #000; margin-top: -10px;">
                          {{ $t('Opening balance:') }}
                        </p></td>
                      <td><p style="font-size: 15px; font-weight: 800;">
                        {{ toFormatAmount(summary.openingBalance) }}
                      </p></td>
                    </tr>
                    <tr>
                      <td>
                        <p style="font-size: 14px; color: #000; margin-top: -10px;">
                          {{ $t('Total Credit:') }}
                        </p>
                      </td>
                      <td>
                        <p style="font-size: 14px; font-weight: 600;">
                          {{ toFormatAmount(summary.totalCredit) }}
                        </p>
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <p style="font-size: 14px; color: #000; margin-top: -10px;">
                          {{ $t('Total Debit:') }}
                        </p>
                      </td>
                      <td>
                        <p style="font-size: 14px; font-weight: 600;">
                          {{ toFormatAmount(summary.totalDebit) }}
                        </p>
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <p style="font-size: 14px; color: #000; margin-top: -10px;">
                          {{ $t('Closing balance:') }}
                        </p></td>
                      <td><p style="font-size: 15px; font-weight: 800;">
                        {{ toFormatAmount(summary.closingBalance) }}
                      </p></td>
                    </tr>
                  </tbody>
                </table>
              </b-card-text>
            </b-card-body>
          </b-col>
        </b-row>

        <b-table
          ref="customerAccountHistoryReportTable"
          select-mode="single"
          :items="reportPrintData"
          responsive
          no-local-sorting
          :fields="translatedPrintTableHeaders"
          primary-key="id"
          show-empty
          :empty-text="$t('No result found')"
          :sort-by.sync="sortBy"
          :sort-desc.sync="sortDesc"
          class="position-relative mt-2"
        >
          <template #table-busy>
            <div class="text-center text-danger my-2">
              <b-spinner
                class="align-middle"
                variant="primary"
              />
            </div>
          </template>
          <template #cell(SN)="data">
            {{ getOverallIndex(data.index) }}
          </template>

          <template #cell(financialDate)="data">
            <p class="mb-0">
              {{ data.value ? formatCustomDate(data.value) : '' }}
            </p>
          </template>

          <template #cell(transactionDate)="data">
            <p class="mb-0">
              {{ data.value ? formatDateTime(data.value) : '' }}
            </p>
          </template>

          <template #cell(balance)="data">
            <span class="text-nowrap">
              {{ toBalance(data.value) }}
            </span>
          </template>
        </b-table>
      </p>
    </div>
    <!-- Print Start-->
  </b-card>

</template>

<script>
import {
  BCard, BCardBody, BCardText, BRow, BCol, BTable, BSpinner, BForm,
  BModal, BDropdown, BDropdownItem,
} from 'bootstrap-vue'
import { ValidationObserver } from 'vee-validate'
import { required } from '@validations'
import { tableMixins } from '@core/mixins/table'
import { permissionMixin } from '@core/mixins/permission'
import { modalMixins } from '@core/mixins/modal'
import { mapGetters, mapActions } from 'vuex'
import Ripple from 'vue-ripple-directive'
// import jsPDF from 'jspdf'
// import 'jspdf-autotable'
// import XLSX from 'xlsx'
import * as FileSaver from 'file-saver'
import {
  toBalance, toFormatAmount, formatAmount,
} from '@/helpers/formatAmount'
import TablePagination from '@/views/components/table/TablePagination.vue'
import TableEntries from '@/views/components/table/TableEntries.vue'
import InputField from '../../../forms/InputField.vue'
import Button from '../../../components/button/Button.vue'
import DatePickerField from '../../../forms/DatePickerField.vue'
import toNumber from '../../../../helpers/toNumber'
import { toCustomDateTime, toCustomDate, toDateWithYearFirst } from '../../../../helpers/formatDate'
import { reportDataDetails } from '../../../../helpers/data'
import { reportPermissions } from '../../../../helpers/permissions'

export default {
  components: {
    BCard,
    BCardBody,
    BCardText,
    BRow,
    BCol,
    Button,
    BSpinner,
    BTable,
    TablePagination,
    TableEntries,
    InputField,
    BModal,
    BDropdown,
    BDropdownItem,
    BForm,
    ValidationObserver,
    DatePickerField,
  },
  directives: {
    Ripple,
  },
  mixins: [modalMixins, tableMixins, permissionMixin],
  data() {
    const now = new Date()
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1)
    const todayDay = new Date(today)
    return {
      toBalance,
      toFormatAmount,
      formatAmount,
      formatToDateWithYears: toDateWithYearFirst,
      firstDate: firstDay,
      todayDate: todayDay,
      query: '',
      statusToggleLabel: '',
      isReadonly: false,
      buttonLabel: this.$t('Save'),
      isEditable: false,
      isDisabled: false,
      isBusy: false,
      required,
      modalTitle: this.$t('Account Balance History Report'),
      sortBy: 'name',
      sortDesc: false,
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'financialDate', sortable: true, label: 'Financial Date' },
        { key: 'transactionDate', sortable: true, label: 'Transaction Date' },
        { key: 'accountNumber', sortable: true, label: 'Account Number' },
        { key: 'accountName', sortable: true, label: 'Account Name' },
        { key: 'postedBy', sortable: true, label: 'Posted By' },
        { key: 'postingReferenceNumber', sortable: true, label: 'Posting Reference Number' },
        { key: 'debit', sortable: true, label: 'Debit' },
        { key: 'credit', sortable: true, label: 'Credit' },
        { key: 'narration', sortable: true, label: 'Narration' },
        { key: 'entryCode', sortable: true, label: 'Sort Code' },
        { key: 'instrumentNumber', sortable: true, label: 'Instrument Number' },
        { key: 'balance', sortable: true, label: 'Balance' },
      ],
      printTableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'financialDate', label: 'Financial Date' },
        { key: 'transactionDate', label: 'Transaction Date' },
        // { key: 'accountNumber', label: 'Account Number' },
        // { key: 'accountName', label: 'Account Name' },
        { key: 'postedBy', label: 'Posted By' },
        { key: 'postingReferenceNumber', label: 'Posting Reference Number' },
        { key: 'debit', label: 'Debit' },
        { key: 'credit', label: 'Credit' },
        { key: 'narration', label: 'Narration' },
        { key: 'entryCode', label: 'Sort Code' },
        { key: 'instrumentNumber', label: 'Instrument Number' },
        { key: 'balance', label: 'Balance' },
      ],
      filter: {
        accountNumber: '',
        referenceAccountNumber: '',
        financialDateFrom: null,
        financialDateTo: null,
      },
      datePickerConfig: {
        dateFormat: 'Y-m-d',
        maxDate: 'today',
        defaultDate: '',
      },
      reportData: [],
      reportPrintData: [],
      summary: {},
      allSearchedData: [],
      selected: '',
      reportInfo: reportDataDetails,
    }
  },
  computed: {
    ...mapGetters({
      branchOptions: 'operations/branchOptions',
      userDetails: 'auth/getCurrentUser',
      corporateInformation: 'operations/getCorporateDetails',
    }),
    translatedTableHeaders() {
      return this.tableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
    translatedPrintTableHeaders() {
      return this.printTableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
  },
  watch: {
    currentPage(newValue) {
      this.currentPage = newValue
      this.fetchAccountBalanceHistoryReport()
    },
    perPage(newValue) {
      this.perPage = newValue
      this.fetchAccountBalanceHistoryReport()
    },
  },
  methods: {
    ...mapActions({
      getAccountHistories: 'reports/getTransactionReports',
      downloadCustomerAccountBalanceHistoryReports: 'reports/downloadCustomerAccountBalanceHistoryReports',
    }),

    async printCustomerHistoryReport() {
      try {
        this.isBusy = true
        const response = await this.getAccountHistories({
          search: this.filter,
          page: 1,
          limit: 100000,
          permission: reportPermissions.ACCOUNT_HISTORY_REPORT,
          route: 'customerHistoryReport',
        })
        const { data, status } = response
        if (status === 200 && data.data) {
          this.reportPrintData = data.data.map(item => ({
            ...item,
            debit: item.debit ? toBalance(item.debit) : '',
            credit: item.credit ? toBalance(item.credit) : '',
            balance: item.balance ? toBalance(item.balance) : '',
          }))
          this.summary = {
            ...data.summary,
            accountName: data.summary.accountName,
          }
          this.totalCount = 0
          setTimeout(() => {
            const { customerHistoryToPrint } = this.$refs
            const printWindow = window.open('', '_blank')
            printWindow.document.write(customerHistoryToPrint.innerHTML)
            const styleElement = printWindow.document.createElement('style')
            styleElement.textContent = `
        body {
          font-family: 'Arial', sans-serif;
          color: #333;
        }
        /* Set A3 size */
          //  @page {
          //   size: A3;
          //   height: 100%;
          //   margin: 5px;
          //   }
        table {
          width: 100%;
          border-collapse: collapse;
          margin-bottom: 20px;
        }

        /* Apply styles to table headers */
        th {
          background-color: #cccccc !important;
          text-transform: uppercase;
          font-size: 14px !important;
        }

        /* Apply styles to table cells */
        td, th {
          padding: 5px;
          text-align: left;
        }

        /* Apply alternating row colors */
        tr:nth-child(even) {
          background-color: #f9f9f9;
        }

        /* Apply hover effect to rows */
        tr:hover {
          background-color: #e5e5e5;
        }
      `
            printWindow.document.head.appendChild(styleElement)
            setTimeout(() => {
              printWindow.document.close()
              printWindow.print()
              printWindow.close()
            }, 1000)
          }, 1000)
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },
    resetFilters() {
      this.filter = { ...null }
    },
    resetFilter() {
      this.filter = { ...null }
      this.fetchAccountBalanceHistoryReport()
    },
    async handleSubmit(bvModalEvt) {
      bvModalEvt.preventDefault()
      this.$refs.accountBalanceHistoryRules.validate().then(async success => {
        if (success) {
          this.fetchAccountBalanceHistoryReport()
        }
      })
    },
    async fetchAccountBalanceHistoryReport() {
      try {
        this.hideModal('modal-filter')
        this.isBusy = true
        const response = await this.getAccountHistories({
          search: this.filter,
          page: this.currentPage,
          limit: this.perPage,
          permission: reportPermissions.ACCOUNT_HISTORY_REPORT,
          route: 'customerHistoryReport',
        })
        const { data, status } = response
        if (status === 200 && data.data) {
          this.isBusy = false
          this.reportData = data.data
          this.totalCount = data.recordCount
          this.summary = {
            ...data.summary,
            accountName: data.summary.accountName,
          }
        } else {
          this.reportData = []
          this.totalCount = 0
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.isBusy = false
      }
    },
    async csvExport() {
      try {
        this.isBusy = true
        const response = await this.downloadCustomerAccountBalanceHistoryReports({
          search: this.filter,
          fileExportFormat: 1,
          permission: reportPermissions.ACCOUNT_HISTORY_REPORT,
        })
        if (response.status === 200) {
          const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8' })
          FileSaver.saveAs(blob, 'Customer Account Balance History Report.csv')
          this.alertSuccess({ text: 'Your request to download Customer Account Balance History Report was successful' })
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },
    async excelExport() {
      try {
        this.isBusy = true
        const response = await this.downloadCustomerAccountBalanceHistoryReports({
          search: this.filter,
          fileExportFormat: 0,
          permission: reportPermissions.ACCOUNT_HISTORY_REPORT,
        })
        if (response.status === 200) {
          const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
          FileSaver.saveAs(blob, 'Customer Account Balance History Report.xlsx')
          this.alertSuccess({ text: 'Your request to download Customer Account Balance History Report was successful' })
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },

    formatNumber: toNumber,
    formatDateTime: toCustomDateTime,
    formatCustomDate: toCustomDate,
  },
}
</script>

<style lang="scss" scoped>
#toPrinter {
  display: none;
}
    .per-page-selector {
        width: 90px;
    }

    .invoice-filter-select {
        min-width: 190px;

        ::v-deep .vs__selected-options{
            flex-wrap: nowrap;
        }

        ::v-deep .vs__selected {
            width: 100px;
        }
    }
</style>

<style lang="scss">
    @import '@core/scss/vue/libs/vue-select.scss';
    @media print {
  html, body {
     height: 99%;
     page-break-after: avoid !important;
     page-break-before: avoid !important;
     visibility:hidden;
  }
  #toPrinter {
    display: block;
  }
}
.print-color {
  color: #000000;
}
img {
  max-width: 50%;
  height: auto;
}
</style>
